import { apiRequest } from '@/domains/api';
import type {
  IIdentifierCheck,
  IIdentifierCheckDecision,
  IIdentifierCheckQuery
} from './identifier-check-contracts';
import type { IPage } from './oversight-contracts';

/**
 * survey-api's number checks (SR-487, its PFR §4.3): `/api/v1/admin/identifier-checks*`, and the
 * sole reader of it. Reads need `responses.browse`; the assignee and the decision
 * `responses.annotate`, as the registration routes do.
 *
 * Its own module rather than a part of `work-items-api.ts`: a check is not a work item — it
 * decides whether a number is the person's, not a compliance outcome — and survey-api keeps it in
 * its own table and file. My tasks reads the same row inside its own answer (`IMyTask.check`).
 */

const BASE = '/api/v1/admin/identifier-checks';

function listQuery(query: IIdentifierCheckQuery): string {
  const params = new URLSearchParams();
  query.status?.forEach((status) => params.append('status', status));
  query.reason?.forEach((reason) => params.append('reason', reason));
  query.idType?.forEach((idType) => params.append('idType', idType));
  if (query.assignee) params.append('assignee', query.assignee);
  if (query.page !== undefined) params.append('page', String(query.page));
  if (query.pageSize !== undefined) params.append('pageSize', String(query.pageSize));
  const rendered = params.toString();
  return rendered ? `?${rendered}` : '';
}

export const identifierChecksApi = {
  /** A page of checks, newest first. 400 `invalid-query` for an unknown status or reason, or an
   *  assignee that is not `me`, `none` or an account id. */
  list(query: IIdentifierCheckQuery, signal?: AbortSignal): Promise<IPage<IIdentifierCheck>> {
    return apiRequest<IPage<IIdentifierCheck>>(`${BASE}${listQuery(query)}`, { signal });
  },

  /** Assigns the check, or unassigns it with null; survey-api stamps `assignedAt`. 409
   *  `check-closed` once decided; 400 `invalid-body` on `accountId` for an account that cannot
   *  work a check. */
  assign(checkId: string, accountId: Nullable<string>): Promise<IIdentifierCheck> {
    return apiRequest<IIdentifierCheck>(`${BASE}/${encodeURIComponent(checkId)}/assignee`, {
      method: 'PUT',
      body: { accountId }
    });
  },

  /**
   * Verifies, rejects or replaces the number (PFR §4.2) and answers the closed check. 409
   * `check-closed` once decided, `identifier-taken` where the number it would write is another
   * participant's (nothing written); 400 `invalid-body` naming `decision`, `value` or
   * `issuingState`.
   */
  decide(checkId: string, decision: IIdentifierCheckDecision): Promise<IIdentifierCheck> {
    return apiRequest<IIdentifierCheck>(`${BASE}/${encodeURIComponent(checkId)}/decision`, {
      method: 'POST',
      body: decision
    });
  }
};
